import { useEffect, useState, useCallback, useRef } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { trpc, MarketingConsentExport } from 'src/trpc';
import { useSearchParams } from 'react-router-dom';
import { SortingState } from '@tanstack/react-table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '../table/DataTable';
import { Loader } from '../Loader';
import { useDebounce } from '../Debounce';
import { MarketingColumns } from './MarketingColumns';
import { useToast } from '../useToast';
import { AlertTriangle, ChevronDown, Download, LoaderCircle, Search } from 'lucide-react';
import { buildMarketingCsv } from './marketingCsv';
import { formatPhone } from 'src/utils/format-phone';

type StatusScope = 'all' | 'opted_in' | 'opted_out';

const DATE_ERROR_ID = 'marketing-date-error';
const EXPORT_HELP_ID = 'marketing-export-help';
const DATE_RANGE_ERROR = 'Start date must be on or before End date.';
const QUERY_ERROR_TITLE_ID = 'marketing-query-error-title';
const QUERY_ERROR_BODY_ID = 'marketing-query-error-body';

const exportMarketingCsv = (records: MarketingConsentExport[], scope: StatusScope) => {
  const csvContent = buildMarketingCsv(records);

  const scopeLabel = scope === 'all' ? 'all' : scope === 'opted_in' ? 'opted-in' : 'opted-out';
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `marketing-consent-${scopeLabel}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const MarketingDashboard = () => {
  const { successToast, errorToast } = useToast();
  const [params, setParams] = useSearchParams();

  const offset = parseInt(params.get('mOffset') || '0', 10) || 0;
  const limit = 25;
  const currentPage = offset === 0 ? 1 : offset / limit + 1;

  const [sorting, setSorting] = useState<SortingState>([{ id: 'optInTimestamp', desc: true }]);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const [startDate, setStartDate] = useState('');
  const [retryState, setRetryState] = useState<'idle' | 'retrying' | 'failed'>('idle');
  const resultsHeadingRef = useRef<HTMLHeadingElement>(null);
  const shouldFocusResultsRef = useRef(false);
  const retryInFlightRef = useRef(false);
  const [endDate, setEndDate] = useState('');

  // Derived rather than stored: both boundaries are inclusive, so a same-day range is valid, and
  // clearing either date has to clear the error in the same render the input changes.
  const hasInvalidDateRange = Boolean(startDate && endDate && startDate > endDate);
  const hasValidDates = !hasInvalidDateRange;

  const sortKey = sorting.length > 0 ? sorting[0].id : 'optInTimestamp';
  const sortDirection = sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : 'desc';

  const {
    data,
    isPending,
    isError,
    isFetching,
    refetch: refetchMarketingConsent,
  } = trpc.admin.marketing.adminListMarketingConsent.useQuery(
    {
      limit,
      offset,
      sortDirection: sortDirection as 'asc' | 'desc',
      sortKey: sortKey === 'optInTimestamp' ? 'optInTimestamp' : 'name',
      search: debouncedSearch || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    },
    {
      // An invalid range must never widen the result set. Dropping the bounds and refetching
      // would quietly return every record, so hold the last valid page until the range is fixed.
      enabled: hasValidDates,
      placeholderData: keepPreviousData,
    },
  );

  useEffect(() => {
    const newParams = new URLSearchParams(params);
    newParams.set('mOffset', '0');
    newParams.set('tab', 'marketing');
    setParams(newParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, startDate, endDate]);

  useEffect(() => {
    const newParams = new URLSearchParams(params);
    newParams.set('mOffset', '0');
    setParams(newParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  const { mutate: exportMutate, isPending: isExporting } =
    trpc.admin.marketing.adminExportMarketingConsent.useMutation({
      onSuccess: (result, variables) => {
        exportMarketingCsv(result.records, variables.statusScope ?? 'all');
        successToast({
          title: 'Export Successful',
          description: `Exported ${result.records.length} marketing consent records.`,
        });
      },
      onError: () => {
        errorToast({
          title: 'Export Failed',
          description: 'There was an error exporting marketing consent data. Please try again.',
        });
      },
    });

  const handleExport = useCallback(
    (scope: StatusScope) => {
      // Guarded as well as disabled. Invalid or not-yet-applied filters must never fall through
      // to a broader export than the operator can see in the inputs.
      if (!hasValidDates || search !== debouncedSearch) return;
      exportMutate({
        search: debouncedSearch || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        statusScope: scope,
      });
    },
    [exportMutate, debouncedSearch, hasValidDates, search, startDate, endDate],
  );

  const handlePageChange = (change: -1 | 1) => {
    const newParams = new URLSearchParams(params);
    newParams.set('mOffset', (offset + change * limit).toString());
    setParams(newParams, { replace: true });
  };

  const handleRetry = async () => {
    if (retryInFlightRef.current || isFetching) return;
    retryInFlightRef.current = true;

    setRetryState('retrying');
    const result = await refetchMarketingConsent();

    retryInFlightRef.current = false;
    if (result.isError) {
      setRetryState('failed');
      return;
    }

    shouldFocusResultsRef.current = true;
    setRetryState('idle');
  };

  useEffect(() => {
    if (!data || isError || !shouldFocusResultsRef.current) return;

    resultsHeadingRef.current?.focus();
    shouldFocusResultsRef.current = false;
  }, [data, isError, retryState]);

  const total = data?.total ?? 0;
  const records = data?.records ?? [];
  const isSearchDebouncing = search !== debouncedSearch;
  const isExportDisabled = isExporting || total === 0 || !hasValidDates || isSearchDebouncing;
  const isRetrying = retryState === 'retrying';

  if (isPending && isFetching && !data && !isRetrying) {
    return <Loader title="Loading marketing consent data" />;
  }

  if (!data && (isError || isRetrying)) {
    return (
      <div className="flex flex-col gap-5">
        <div>
          <h2 className="text-xl font-semibold">Marketing SMS consent</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Customers with marketing consent history, including current opt-in and opt-out status.
          </p>
        </div>
        <div
          className="mx-auto mt-2 grid w-full max-w-[720px] justify-items-center rounded-lg border border-destructive/30 bg-destructive/5 px-5 py-9 text-center sm:px-8 sm:py-12"
          role="alert"
          aria-labelledby={QUERY_ERROR_TITLE_ID}
          aria-describedby={QUERY_ERROR_BODY_ID}
          aria-busy={isRetrying || undefined}
        >
          <span className="grid h-[42px] w-[42px] place-items-center rounded-full bg-destructive/10">
            <AlertTriangle aria-hidden="true" className="h-6 w-6 text-destructive" />
          </span>
          <h3 id={QUERY_ERROR_TITLE_ID} className="mt-4 text-lg font-semibold">
            Marketing consent data couldn’t load
          </h3>
          <p id={QUERY_ERROR_BODY_ID} className="mt-2 max-w-[460px] text-sm text-muted-foreground">
            We couldn’t load the consent history. Check your connection and try again.
          </p>
          <Button
            className="mt-5 min-h-11 w-full sm:w-auto"
            variant="outline"
            aria-disabled={isRetrying || undefined}
            onClick={() => void handleRetry()}
          >
            {isRetrying && (
              <LoaderCircle aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
            )}
            {isRetrying ? 'Trying again…' : 'Try again'}
          </Button>
        </div>
        <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {isRetrying
            ? 'Trying to load marketing consent data.'
            : retryState === 'failed'
              ? 'Marketing consent data still couldn’t load. Try again.'
              : ''}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold">Marketing SMS consent</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Customers with marketing consent history, including current opt-in and opt-out status.
          </p>
        </div>
        <div className="flex flex-col items-stretch gap-1.5 sm:items-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                disabled={isExportDisabled}
                className="w-full sm:w-auto"
                aria-describedby={
                  hasInvalidDateRange ? `${EXPORT_HELP_ID} ${DATE_ERROR_ID}` : EXPORT_HELP_ID
                }
              >
                <Download className="mr-2 h-4 w-4" />
                {isExporting ? 'Exporting...' : 'Export'}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[210px]">
              <DropdownMenuItem onClick={() => handleExport('all')}>Export All</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('opted_in')}>
                Export Opt-in only
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('opted_out')}>
                Export Opt-out only
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* One helper node, one id, so the Export trigger can describe itself the same way in
              every state. Swapping the text rather than the element keeps that reference stable. */}
          <span
            id={EXPORT_HELP_ID}
            className={`text-xs text-left sm:text-right ${
              hasInvalidDateRange ? 'font-medium text-destructive' : 'text-muted-foreground'
            }`}
          >
            {hasInvalidDateRange
              ? 'Fix the date range to enable export.'
              : isSearchDebouncing
                ? 'Wait for search results to update before exporting.'
                : total === 0
                  ? 'No matching records to export.'
                  : 'Each export includes all rows matching search and date filters'}
          </span>
        </div>
      </div>

      {/* Viewport breakpoints that account for the 256px sidebar at desktop widths. The old sm
          (640px) three-across row needed more width than the viewport had, which clipped End
          date. Dates share a row from 520px and go three-across only once lg leaves room. */}
      <div className="grid grid-cols-1 gap-3 min-[520px]:grid-cols-2 lg:grid-cols-[minmax(260px,1fr)_190px_190px] lg:items-end">
        <div className="space-y-1.5 min-w-0 min-[520px]:col-span-2 lg:col-span-1">
          <label htmlFor="marketing-search" className="text-xs font-medium text-gray-600">
            Search
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              id="marketing-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by customer name or phone number"
              className="pl-9"
              aria-label="Search marketing consent history"
            />
          </div>
        </div>
        {/* Both inputs carry the invalid state and point at the same error: the invalid
            relationship belongs to the pair, not to whichever field was typed last. */}
        <div className="space-y-1.5 min-w-0">
          <label htmlFor="marketing-start-date" className="text-xs font-medium text-gray-600">
            Start date
          </label>
          <Input
            id="marketing-start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            aria-label="Start date"
            aria-invalid={hasInvalidDateRange || undefined}
            aria-describedby={hasInvalidDateRange ? DATE_ERROR_ID : undefined}
            className={
              hasInvalidDateRange ? 'border-destructive focus-visible:ring-destructive' : undefined
            }
          />
        </div>
        <div className="space-y-1.5 min-w-0">
          <label htmlFor="marketing-end-date" className="text-xs font-medium text-gray-600">
            End date
          </label>
          <Input
            id="marketing-end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            aria-label="End date"
            aria-invalid={hasInvalidDateRange || undefined}
            aria-describedby={hasInvalidDateRange ? DATE_ERROR_ID : undefined}
            className={
              hasInvalidDateRange ? 'border-destructive focus-visible:ring-destructive' : undefined
            }
          />
        </div>
      </div>
      {/* A single role="alert" and no second live region: adding aria-live here as well makes
          screen readers announce the same error twice. The icon carries the state alongside
          colour so the error does not rely on colour alone. */}
      {hasInvalidDateRange && (
        <p
          id={DATE_ERROR_ID}
          role="alert"
          className="flex items-center gap-1.5 text-sm font-medium text-destructive"
        >
          <AlertTriangle aria-hidden="true" className="h-4 w-4 shrink-0" />
          {DATE_RANGE_ERROR}
        </p>
      )}

      <h3 ref={resultsHeadingRef} tabIndex={-1} className="sr-only">
        Marketing consent results
      </h3>

      {/* Desktop/Tablet table */}
      <div className="hidden lg:block">
        <DataTable
          total={total}
          resourceName="customers with consent history"
          offset={offset}
          limit={limit}
          columns={MarketingColumns}
          data={records}
          sorting={sorting}
          onSortingChange={setSorting}
          hasNextPage={total > offset + limit}
          hasPreviousPage={offset > 0}
          onPageChange={handlePageChange}
          currentPage={currentPage}
          pageCount={Math.ceil(total / limit)}
        />
      </div>

      {/* Mobile cards */}
      <div className="block lg:hidden space-y-2.5">
        {records.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">
            No customers with consent history match these filters.
          </p>
        ) : (
          <>
            {records.map((record) => (
              <article key={record._id} className="rounded-md border border-gray-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-gray-900">{record.name || 'Unknown'}</div>
                    <div className="mt-1 text-sm text-gray-600 tabular-nums">
                      {formatPhone(record.phone || '')}
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      record.status === 'opted_in'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {record.status === 'opted_in' ? 'Opted in' : 'Opted out'}
                  </span>
                </div>
                <div className="mt-3 border-t border-gray-200 pt-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                    Opt-in date
                  </div>
                  <div className="mt-1 text-sm text-gray-700">
                    {record.optInTimestamp
                      ? new Date(record.optInTimestamp).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '-'}
                  </div>
                </div>
              </article>
            ))}
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>
                Showing {offset + 1}-{Math.min(offset + limit, total)} of {total}
              </span>
              <div className="flex gap-2">
                {offset > 0 && (
                  <Button variant="outline" size="sm" onClick={() => handlePageChange(-1)}>
                    Previous
                  </Button>
                )}
                {total > offset + limit && (
                  <Button variant="outline" size="sm" onClick={() => handlePageChange(1)}>
                    Next
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
